/**
 * usePortfolioReport — data hook for the Portfolio Health Report page.
 *
 * Fetches GET /api/portfolio/report/latest, exposes a discriminated union of
 * fetch outcomes, polls every 5s while generation is in progress (capped at
 * 60 attempts / 5 minutes), and drives the "Regenerate" flow via
 * POST /api/portfolio/report/trigger.
 *
 * See packages/web/src/__tests__/unit/usePortfolioReport.test.ts for the
 * full implementation contract this hook satisfies.
 */

import { useCallback, useEffect, useRef, useState } from "react";
import { apiGet, apiPost } from "../lib/api.js";
import { parseRollup } from "../types/portfolio.js";
import type { PortfolioReportResponse, PortfolioRollup } from "../types/portfolio.js";

// ---------------------------------------------------------------------------
// Types
// ---------------------------------------------------------------------------

export type PortfolioReportState =
	| { status: "loading" }
	| { status: "denied"; deniedStatus: 401 | 403 }
	| { status: "empty" }
	| { status: "error"; message: string }
	| {
			status: "success";
			report: PortfolioReportResponse;
			rollup: PortfolioRollup | null;
			inProgress: boolean;
			pollTimedOut: boolean;
	  };

export interface UsePortfolioReportResult {
	state: PortfolioReportState;
	isRegenerating: boolean;
	regenerate: () => Promise<void>;
	refetch: () => void;
}

type FetchOutcome =
	| { kind: "success"; report: PortfolioReportResponse }
	| { kind: "denied"; status: 401 | 403 }
	| { kind: "empty" }
	| { kind: "error"; message: string };

// ---------------------------------------------------------------------------
// Constants
// ---------------------------------------------------------------------------

const LATEST_PATH = "/api/portfolio/report/latest";
const TRIGGER_PATH = "/api/portfolio/report/trigger";

const POLL_INTERVAL_MS = 5_000;
const MAX_GENERATION_POLLS = 60; // 60 x 5s = 5 minutes

const IN_PROGRESS_STATUSES = new Set([
	"pending",
	"aggregating",
	"collecting",
	"computing",
	"generating",
]);
const TERMINAL_STATUSES = new Set(["complete", "failed"]);

// ---------------------------------------------------------------------------
// Hook
// ---------------------------------------------------------------------------

export function usePortfolioReport(): UsePortfolioReportResult {
	const [state, setState] = useState<PortfolioReportState>({ status: "loading" });
	const [isRegenerating, setIsRegenerating] = useState(false);

	/** Guards against unmount races — checked after every await. */
	const unmountedRef = useRef(false);

	/** Timer for the "generation in progress" polling loop (initial fetch → terminal). */
	const pollTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
	/** Counts interval-triggered polls only — the initial mount fetch does not count. */
	const pollCountRef = useRef(0);

	/** Timer for the separate "regenerate" polling loop. */
	const regenPollTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
	/** Synchronous debounce guard — set true the instant regenerate() is called. */
	const regeneratingRef = useRef(false);
	/** `updated_at` of the report at the moment regenerate() was called. */
	const regenSnapshotRef = useRef<string | null>(null);
	/** Mirrors the last successfully fetched report, read synchronously by regenerate(). */
	const latestReportRef = useRef<PortfolioReportResponse | null>(null);

	const clearGenerationPollTimer = useCallback(() => {
		if (pollTimerRef.current !== null) {
			clearTimeout(pollTimerRef.current);
			pollTimerRef.current = null;
		}
	}, []);

	const clearRegenPollTimer = useCallback(() => {
		if (regenPollTimerRef.current !== null) {
			clearTimeout(regenPollTimerRef.current);
			regenPollTimerRef.current = null;
		}
	}, []);

	/** Performs the GET and classifies the response into a discriminated outcome. Never throws. */
	const fetchLatest = useCallback(async (): Promise<FetchOutcome> => {
		try {
			const response = await apiGet(LATEST_PATH);

			if (response.status === 401 || response.status === 403) {
				return { kind: "denied", status: response.status as 401 | 403 };
			}
			if (response.status === 404) {
				return { kind: "empty" };
			}
			if (!response.ok) {
				return { kind: "error", message: `Request failed with status ${response.status}` };
			}

			const report = (await response.json()) as PortfolioReportResponse;
			return { kind: "success", report };
		} catch (err) {
			const message = err instanceof Error ? err.message : String(err);
			return { kind: "error", message: `Unable to load the portfolio report: ${message}` };
		}
	}, []);

	/** Records the report and derives inProgress/rollup, then commits the success state. */
	const applySuccess = useCallback((report: PortfolioReportResponse, pollTimedOut: boolean) => {
		latestReportRef.current = report;
		const rollup = parseRollup(report.rollup_data);
		const inProgress = IN_PROGRESS_STATUSES.has(report.generation_status);
		setState({ status: "success", report, rollup, inProgress, pollTimedOut });
	}, []);

	/** Drives the initial fetch and the "generation in progress" polling loop. */
	const runGenerationPoll = useCallback(
		async (mode: "initial" | "generation-poll") => {
			if (mode === "generation-poll") {
				pollCountRef.current += 1;
			}

			const outcome = await fetchLatest();
			if (unmountedRef.current) return;

			if (outcome.kind === "denied") {
				setState({ status: "denied", deniedStatus: outcome.status });
				return;
			}
			if (outcome.kind === "empty") {
				setState({ status: "empty" });
				return;
			}
			if (outcome.kind === "error") {
				// A transient error mid-poll should not discard a previously successful
				// report — only the very first fetch is allowed to surface a hard error.
				if (mode === "initial") {
					setState({ status: "error", message: outcome.message });
					return;
				}
				// Background poll failed transiently — keep the last known success
				// state untouched and just retry on the same cadence, respecting the
				// same cap as a normal in-progress poll.
				if (pollCountRef.current >= MAX_GENERATION_POLLS) {
					const last = latestReportRef.current;
					if (last) {
						applySuccess(last, true);
					}
					return;
				}
				pollTimerRef.current = setTimeout(() => {
					pollTimerRef.current = null;
					void runGenerationPoll("generation-poll");
				}, POLL_INTERVAL_MS);
				return;
			}

			const { report } = outcome;
			const terminal = TERMINAL_STATUSES.has(report.generation_status);

			if (terminal) {
				applySuccess(report, false);
				return;
			}

			if (mode === "generation-poll" && pollCountRef.current >= MAX_GENERATION_POLLS) {
				applySuccess(report, true);
				return;
			}

			applySuccess(report, false);
			pollTimerRef.current = setTimeout(() => {
				pollTimerRef.current = null;
				void runGenerationPoll("generation-poll");
			}, POLL_INTERVAL_MS);
		},
		[fetchLatest, applySuccess],
	);

	/** Drives the "regenerate" polling loop, independent of the generation-poll cap/counter. */
	const runRegenPoll = useCallback(async () => {
		const outcome = await fetchLatest();
		if (unmountedRef.current) return;

		if (outcome.kind !== "success") {
			// Cannot determine the outcome — stop regenerating rather than hang forever.
			regeneratingRef.current = false;
			setIsRegenerating(false);
			return;
		}

		const { report } = outcome;
		const terminal = TERMINAL_STATUSES.has(report.generation_status);
		const changed =
			regenSnapshotRef.current === null || report.updated_at !== regenSnapshotRef.current;

		if (terminal && changed) {
			applySuccess(report, false);
			regeneratingRef.current = false;
			setIsRegenerating(false);
			return;
		}

		// Either a stale row (unchanged updated_at) or still non-terminal — keep waiting.
		applySuccess(report, false);
		regenPollTimerRef.current = setTimeout(() => {
			regenPollTimerRef.current = null;
			void runRegenPoll();
		}, POLL_INTERVAL_MS);
	}, [fetchLatest, applySuccess]);

	const regenerate = useCallback(async (): Promise<void> => {
		if (regeneratingRef.current) return; // debounce — ignore calls while already regenerating
		regeneratingRef.current = true;
		setIsRegenerating(true);
		regenSnapshotRef.current = latestReportRef.current?.updated_at ?? null;

		// Stop any still-running generation poll (e.g. the loaded report was
		// itself in-progress) so the two polling loops never run concurrently.
		clearGenerationPollTimer();

		try {
			await apiPost(TRIGGER_PATH, {});
		} catch {
			// Swallow — still attempt to poll for the eventual outcome.
		}

		if (unmountedRef.current) {
			regeneratingRef.current = false;
			setIsRegenerating(false);
			return;
		}

		regenPollTimerRef.current = setTimeout(() => {
			regenPollTimerRef.current = null;
			void runRegenPoll();
		}, POLL_INTERVAL_MS);
	}, [runRegenPoll, clearGenerationPollTimer]);

	const refetch = useCallback(() => {
		clearGenerationPollTimer();
		pollCountRef.current = 0;
		void runGenerationPoll("initial");
	}, [clearGenerationPollTimer, runGenerationPoll]);

	useEffect(() => {
		unmountedRef.current = false;
		void runGenerationPoll("initial");

		return () => {
			unmountedRef.current = true;
			clearGenerationPollTimer();
			clearRegenPollTimer();
		};
		// Intentionally run once on mount — runGenerationPoll's identity is stable
		// across renders (its own deps are stable), and re-running on every render
		// would restart polling.
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, []);

	return { state, isRegenerating, regenerate, refetch };
}
